import React, { useState, useEffect } from 'react';
import {
  ServiceRequest,
  ServiceRequestStatus,
  ServiceRequestPriority,
  PricingPlan,
} from '../../types';
import { serviceMarketplaceService } from '../../services/serviceMarketplaceService';
import {
  Briefcase,
  Smartphone,
  Code2,
  TrendingUp,
  MapPin,
  Globe,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Tag,
  Save,
  MessageSquare,
  Send,
  User,
  Phone,
  Mail,
  Filter,
  Check,
  RotateCw,
} from 'lucide-react';

export const AdminServiceRequestsView: React.FC = () => {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [selectedReq, setSelectedReq] = useState<ServiceRequest | null>(null);
  const [replyText, setReplyText] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await serviceMarketplaceService.getRequests(undefined, true);
      setRequests(data);
      if (selectedReq) {
        const refreshed = data.find((r) => r.id === selectedReq.id);
        if (refreshed) setSelectedReq(refreshed);
      }
    } catch (err) {
      console.error('Failed to load service requests in admin:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleStatusChange = async (requestId: string, newStatus: ServiceRequestStatus) => {
    setStatusUpdating(true);
    try {
      await serviceMarketplaceService.updateRequest(requestId, {
        status: newStatus,
        adminNotes: internalNote || undefined,
      });
      setFeedback('Talep durumu başarıyla güncellendi.');
      setTimeout(() => setFeedback(null), 3000);
      await loadRequests();
    } catch (err: any) {
      setFeedback('Hata: Durum güncellenirken sorun oluştu: ' + (err.message || ''));
      setTimeout(() => setFeedback(null), 4000);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleSendReply = async () => {
    if (!selectedReq || !replyText.trim()) return;
    try {
      await serviceMarketplaceService.addMessage(
        selectedReq.id,
        'ADIOSOFT',
        'Adiosoft Operasyon & Çözüm Ekibi',
        replyText.trim()
      );
      setReplyText('');
      setFeedback('Müşteriye yanıt iletildi.');
      setTimeout(() => setFeedback(null), 3000);
      await loadRequests();
    } catch (err: any) {
      setFeedback('Hata: Mesaj gönderilemedi: ' + (err.message || ''));
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const filtered = requests.filter((r) => {
    if (statusFilter === 'ALL') return true;
    return r.status === statusFilter;
  });

  const getStatusBadge = (status: ServiceRequestStatus) => {
    switch (status) {
      case 'NEW':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">Yeni Talep</span>;
      case 'REVIEWING':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800">İnceleniyor</span>;
      case 'PREPARING_QUOTE':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800">Teklif Hazırlanıyor</span>;
      case 'AWAITING_CLIENT_INFO':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">Müşteri Bekleniyor</span>;
      case 'IN_PROGRESS':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">İşlemde</span>;
      case 'COMPLETED':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-teal-100 text-teal-800">Tamamlandı</span>;
      case 'CANCELLED':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">İptal Edildi</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <Briefcase className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">Müşteri Dijital Hizmet Talepleri</h3>
            <p className="text-xs text-slate-500">
              Mobil uygulama, özel yazılım ve harita optimizasyon taleplerini yönetin.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadRequests}
            className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            title="Yenile"
          >
            <RotateCw className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl text-xs">
            {['ALL', 'NEW', 'REVIEWING', 'IN_PROGRESS', 'COMPLETED'].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`px-3 py-1.5 rounded-lg font-bold transition ${
                  statusFilter === st ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {st === 'ALL'
                  ? `Tümü (${requests.length})`
                  : st === 'NEW'
                  ? 'Yeni'
                  : st === 'REVIEWING'
                  ? 'İncelemede'
                  : st === 'IN_PROGRESS'
                  ? 'İşlemde'
                  : 'Tamamlandı'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {feedback && (
        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          {feedback}
        </div>
      )}

      {/* Main Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Request List (1 col) */}
        <div className="lg:col-span-1 space-y-3">
          {loading ? (
            <div className="p-8 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
              Talepler yükleniyor...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
              Bu filtrede talep bulunmuyor.
            </div>
          ) : (
            filtered.map((req) => (
              <div
                key={req.id}
                onClick={() => setSelectedReq(req)}
                className={`p-4 rounded-2xl border cursor-pointer transition ${
                  selectedReq?.id === req.id
                    ? 'bg-blue-50/60 border-blue-500 shadow-sm ring-1 ring-blue-500/20'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-bold text-slate-900 line-clamp-1">{req.title}</span>
                  {getStatusBadge(req.status)}
                </div>

                <div className="mt-2 text-xs text-slate-600 line-clamp-2">{req.description}</div>

                <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="font-semibold text-slate-700">{req.contactName}</span>
                  <span>{new Date(req.createdAt).toLocaleDateString('tr-TR')}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Right: Request Details & Action Panel (2 cols) */}
        <div className="lg:col-span-2">
          {selectedReq ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
                      {selectedReq.serviceType}
                    </span>
                    <span className="text-slate-300">•</span>
                    <span className="text-xs font-mono text-slate-400">{selectedReq.id}</span>
                  </div>
                  <h2 className="text-lg font-black text-slate-900 mt-1">{selectedReq.title}</h2>
                </div>

                {/* Status Switcher */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-500">Durum:</span>
                  <select
                    value={selectedReq.status}
                    disabled={statusUpdating}
                    onChange={(e) => handleStatusChange(selectedReq.id, e.target.value as ServiceRequestStatus)}
                    className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-800 focus:outline-hidden focus:border-blue-500"
                  >
                    <option value="NEW">Yeni Talep</option>
                    <option value="REVIEWING">İnceleniyor</option>
                    <option value="PREPARING_QUOTE">Teklif Hazırlanıyor</option>
                    <option value="AWAITING_CLIENT_INFO">Müşteriden Bilgi Bekleniyor</option>
                    <option value="IN_PROGRESS">İşlemde / Geliştiriliyor</option>
                    <option value="COMPLETED">Tamamlandı & Yayında</option>
                    <option value="CANCELLED">İptal Edildi</option>
                  </select>
                </div>
              </div>

              {/* Customer Contact & Meta Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100 text-xs">
                <div>
                  <span className="text-slate-400 block mb-0.5 font-medium">Müşteri / Yetkili</span>
                  <div className="font-bold text-slate-800 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-slate-500" />
                    {selectedReq.contactName}
                  </div>
                </div>
                <div>
                  <span className="text-slate-400 block mb-0.5 font-medium">Telefon</span>
                  <div className="font-bold text-slate-800 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-slate-500" />
                    {selectedReq.contactPhone}
                  </div>
                </div>
                <div>
                  <span className="text-slate-400 block mb-0.5 font-medium">E-Posta</span>
                  <div className="font-bold text-slate-800 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-slate-500" />
                    {selectedReq.contactEmail}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Talep Açıklaması</h4>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-xs text-slate-700 leading-relaxed">
                  {selectedReq.description}
                </div>
              </div>

              {/* Extra Details / Specs */}
              {selectedReq.details && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Teknik Detaylar & Hedefler</h4>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {selectedReq.details.platform && (
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <span className="text-slate-400 block text-[11px]">Hedef Platform</span>
                        <span className="font-bold text-slate-800">{selectedReq.details.platform}</span>
                      </div>
                    )}
                    {selectedReq.details.targetAudience && (
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <span className="text-slate-400 block text-[11px]">Hedef Kitle</span>
                        <span className="font-bold text-slate-800">{selectedReq.details.targetAudience}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Internal Notes */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Dahili Ekip Notu</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Ekip için özel operasyon notu yazın..."
                    className="flex-1 bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:border-blue-500 font-medium"
                  />
                  <button
                    onClick={() => handleStatusChange(selectedReq.id, selectedReq.status)}
                    className="px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition"
                  >
                    Notu Kaydet
                  </button>
                </div>
                {selectedReq.adminNotes && (
                  <p className="text-[11px] text-slate-500 italic bg-amber-50 p-2.5 rounded-lg border border-amber-200">
                    Kayıtlı not: {selectedReq.adminNotes}
                  </p>
                )}
              </div>

              {/* Message Thread */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                  Müşteri Mesajlaşma Geçmişi
                </h4>

                <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-xl border border-slate-100">
                  {(!selectedReq.messages || selectedReq.messages.length === 0) ? (
                    <div className="text-center py-4 text-slate-400 text-xs">Henüz mesaj iletilmedi.</div>
                  ) : (
                    selectedReq.messages.map((m) => (
                      <div
                        key={m.id}
                        className={`p-3 rounded-xl text-xs max-w-lg ${
                          m.sender === 'ADIOSOFT'
                            ? 'ml-auto bg-blue-600 text-white'
                            : 'mr-auto bg-white border border-slate-200 text-slate-800'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1 opacity-80 text-[10px]">
                          <span className="font-bold">{m.senderName}</span>
                          <span>{new Date(m.timestamp).toLocaleTimeString('tr-TR')}</span>
                        </div>
                        <div>{m.message}</div>
                      </div>
                    ))
                  )}
                </div>

                {/* Reply Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendReply()}
                    placeholder="Müşteriye resmi yanıt iletin..."
                    className="flex-1 bg-white border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder:text-slate-400 font-medium focus:outline-hidden focus:border-blue-500"
                  />
                  <button
                    onClick={handleSendReply}
                    className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-1.5"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Gönder
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-16 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
              Detaylarını ve mesajlaşma geçmişini görüntülemek için sol listeden bir talep seçin.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdminPricingPlansView: React.FC = () => {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await serviceMarketplaceService.getPricingPlans();
      setPlans(data);
    } catch (err) {
      console.error('Failed to load pricing plans in admin:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleStartEdit = (plan: PricingPlan) => {
    setEditingId(plan.id);
    setEditPrice(plan.priceTRY);
  };

  const handleSavePrice = async (planId: string) => {
    setSaving(true);
    try {
      await serviceMarketplaceService.updatePricingPlan(planId, { priceTRY: editPrice });
      setEditingId(null);
      setToast('Fiyatlandırma başarıyla güncellendi ve kaydedildi.');
      setTimeout(() => setToast(null), 3500);
      await loadPlans();
    } catch (err: any) {
      setToast('Hata: Fiyat güncellenirken sorun: ' + (err.message || ''));
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Tag className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">Platform Fiyatlandırma & Paket Yönetimi</h3>
            <p className="text-xs text-slate-500">
              Fiyatlar kodun içine sabitlenmemiştir. Buradan güncellediğinizde müşteriler anında yeni fiyatı görür.
            </p>
          </div>
        </div>

        <button
          onClick={loadPlans}
          className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition flex items-center gap-1.5 text-xs font-bold"
        >
          <RotateCw className="w-4 h-4" />
          Yenile
        </button>
      </div>

      {toast && (
        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          {toast}
        </div>
      )}

      {/* Pricing Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          <div className="col-span-3 p-12 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
            Fiyatlandırma planları yükleniyor...
          </div>
        ) : (
          plans.map((plan) => {
            const isEditing = editingId === plan.id;
            return (
              <div
                key={plan.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col justify-between hover:border-slate-300 transition"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                      {plan.serviceType}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                      {plan.period === 'ANNUAL'
                        ? 'Yıllık Lisans'
                        : plan.period === 'MONTHLY'
                        ? 'Aylık Hizmet'
                        : 'Proje Bazlı'}
                    </span>
                  </div>

                  <div>
                    <h4 className="font-bold text-slate-900 text-base">{plan.name}</h4>
                    <p className="text-xs text-slate-500 mt-1">{plan.description}</p>
                  </div>

                  {/* Feature list */}
                  <div className="space-y-1.5 pt-2 border-t border-slate-100 text-xs text-slate-600">
                    {plan.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Price edit zone */}
                <div className="mt-5 pt-4 border-t border-slate-100">
                  {isEditing ? (
                    <div className="space-y-2">
                      <label className="text-[11px] font-bold text-slate-600 block">Yeni Fiyat (TL):</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={editPrice}
                          onChange={(e) => setEditPrice(Number(e.target.value))}
                          className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-1.5 text-sm font-bold text-slate-900 focus:outline-hidden focus:border-blue-500"
                        />
                        <button
                          disabled={saving}
                          onClick={() => handleSavePrice(plan.id)}
                          className="px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition flex items-center gap-1 shadow-sm"
                        >
                          <Save className="w-3.5 h-3.5" />
                          Kaydet
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-2.5 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200"
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs text-slate-400 block font-medium">Güncel Fiyat</span>
                        <div className="text-xl font-extrabold text-slate-900">
                          {plan.priceTRY.toLocaleString('tr-TR')} TL
                          <span className="text-xs font-medium text-slate-500 ml-1">
                            {plan.period === 'ANNUAL' ? '/ yıl' : plan.period === 'MONTHLY' ? '/ ay' : ''}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleStartEdit(plan)}
                        className="px-3 py-1.5 rounded-xl border border-slate-300 hover:border-slate-400 text-slate-700 text-xs font-bold transition"
                      >
                        Fiyatı Düzenle
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
